/**
 * 3rd Eye — Revenue Intelligence. Charcoal + Bright Blue + white/gray light theme.
 * Futura PT display (self-hosted; Jost is now only its fallback), Roboto body, Bodoni Moda
 * italic emphasis, DM Mono ref codes.
 * Spec: docs/superpowers/specs/2026-09-01-third-eye-rebrand-design.md
 *
 * NOTE (transition): legacy key names (charcoal, brightBlue, pageBg, blueMuted…) are kept and
 * re-pointed to 3rd Eye values so all usages restyle in one sweep; a mechanical
 * rename lands as a follow-up PR. charcoal→charcoal, brightBlue→bright blue, pageBg→gray.
 */
export const digitalzoneTokens = {
  // ── Brand ──
  charcoal: '#322F2F', // text, dark surfaces
  charcoalDim: '#4B4848', // charcoal hover/lift
  brightBlue: '#4271EF', // links (large), stats, active accents. NEVER a button fill

  // ── 3rd Eye additions: brand ──
  linkBlue: '#3462E0',
  navy: '#1C2D46', // accent only — never a background
  buttonFill: '#212121', // primary button bg (pill)
  buttonHover: '#AAAAAA', // button hover fill (light + dark) — per style card

  // ── Surfaces ──
  pageBg: '#FAFAFA', // subtle fill (was also the page background)
  pageGround: '#F0F0F0', // page background behind the cards (NPJB-852)
  cardBg: '#F5F5F4', // card surface
  panelHeadBg: '#FCFCFB', // data panel header and footer band (NPJB-852)
  white: '#FEFFFF', // panel white

  // ── Text ──
  body: '#322F2F',
  muted: '#6B6B6B', // AA-minimum muted on light surfaces
  // Onboarding-flow placeholders (~2.3:1). Deliberately under the text floor:
  // a placeholder is a transient hint, and at `muted` a long one read as a
  // value already filled in. Not for content text.
  fieldPlaceholder: '#AAAAAA',
  blueMuted: '#55647D', // secondary text — blue-muted
  onPrimary: '#FEFFFF', // label on primary (charcoal) button
  onCharcoal: '#FEFFFF', // text on charcoal surfaces

  // ── Borders (1px hairlines only) ──
  border: '#E4E4E4', // dividers + white/neutral card edges
  borderLight: '#EDEDED', // panel edges, KPI strip, table row dividers (NPJB-852)
  sectionRule: '#C9C9C9', // the hairline after a Home section eyebrow, at half opacity (NPJB-897)
  changeUp: '#3D6457', // Home results change chip, rising (NPJB-897, prototype --c-pos)
  changeDown: '#A8474C', // Home results change chip, falling (NPJB-897, prototype --c-neg)
  rowHover: '#F7F9FE', // Home activity feed row, hover and focus (NPJB-898, prototype)
  skeleton: '#EFEFEF', // Home activity feed loading bars (NPJB-898, prototype)
  skeletonShine: '#F7F7F7', // the shimmer across those bars (NPJB-898)
  borderDim: '#8F8F8F', // interactive field borders
  borderSoft: 'rgba(228, 228, 228, 0.6)',
  borderSoftDim: 'rgba(228, 228, 228, 0.4)',

  // ── On-charcoal overlays (translucent UI on charcoal surfaces) ──
  onCharcoalSoft: 'rgba(255, 255, 255, 0.7)',
  onCharcoalFaint: 'rgba(255, 255, 255, 0.4)',
  onCharcoalOverlayBg: 'rgba(255, 255, 255, 0.10)',
  onCharcoalOverlayBorder: 'rgba(255, 255, 255, 0.20)',
  onCharcoalOverlayHover: 'rgba(255, 255, 255, 0.12)',
  // The brand blue lightened enough to clear AA on charcoal: Bright Blue itself is only
  // 3.06:1 there and Link Blue 2.51:1, so a dark surface needs its own blue.
  onCharcoalAccent: '#A1B8F7',

  // ── Blue tints (hover, selected, chip backgrounds) ──
  blueTint5: 'rgba(66, 113, 239, 0.05)',
  blueTint10: 'rgba(66, 113, 239, 0.10)',
  blueTint12: 'rgba(66, 113, 239, 0.12)',
  blueTint40: 'rgba(66, 113, 239, 0.40)',

  // ── Blue-muted tints (soft accents) ──
  blueMutedTint10: 'rgba(85, 100, 125, 0.10)',
  blueMutedTint40: 'rgba(85, 100, 125, 0.40)',

  // ── Modal mask (charcoal at 50%) ──
  maskCharcoal: 'rgba(50, 47, 47, 0.5)',

  // ── Auth glass card (frosted panel over the aurora background) ──
  authGlass: 'rgba(255, 255, 255, 0.78)',
  // The aurora photograph behind login and the onboarding sign-up card
  authBgImage: "url('/aurora-bg.jpg')",

  // ── Blue tint scale (one-series shading) ──
  blue100: '#4271EF',
  blue75: '#7194F3',
  blue50: '#A0B8F7',
  blue25: '#CFDBFB',

  // ── Semantic state colors (sage / coral / orange families) ──
  success: '#4E7D6E',
  successBg: '#E6EFE6',
  successBorder: '#9CC0AC',
  warning: '#DD8434',
  warningBg: '#FFEADB',
  warningBorder: '#FFD0B1',
  error: '#8F2F3A',
  errorBg: '#FFE8E9',
  errorBorder: '#F0BABC',
  blocked: '#A8474C', // NPJB-851: a download the browser refused — lighter than error
  blockedBg: '#FFE8E9',
  blockedBorder: '#FFB7BA',
  info: '#3462E0',
  infoBg: '#D9E3FC',
  infoBorder: '#A1B8F7',
  infoBorderSoft: '#BACCF7', // summary chip edge (NPJB-852)
  advanced: '#1C2D46',
  advancedBg: '#D9E3FC',

  // ── Disabled / inactive (flat tokens — opacity is not the mechanism) ──
  disabledFill: '#DCDCDC', // filled button bg
  disabledFillLabel: '#5A5A5A', // filled button label (5.0:1)
  disabledBorder: '#C9C9C9', // outline button / field border
  disabledText: '#767676', // outline label, field text, nav/links (≥4:1)
  disabledFieldFill: '#F5F5F5', // fields & selects — never an empty white box

  // ── Password strength scale (card semantic families: coral → orange → sage) ──
  strengthVeryWeak: '#8F2F3A',
  strengthWeak: '#E58589',
  strengthFair: '#DD8434',
  strengthStrong: '#4E7D6E',
  strengthVeryStrong: '#33502F',

  // ── Audience-status tone palettes (badge + filter-chip + audience-card accent).
  //    Remapped to the 6 tonal-card families (NPJB-640): gray→Neutral, brightBlue→Peach,
  //    teal/blue→Blue, emerald/green→Sage, purple→Blue(navy). Each tone keeps bg+fg+border.
  toneGrayBg: '#F5F5F4',
  toneGrayFg: '#6B6B6B',
  toneGrayDarkFg: '#322F2F',
  toneGrayBorder: '#E4E4E4',
  tonePeachBg: '#FFEADB',
  tonePeachFg: '#8A4A12',
  tonePeachBorder: '#FFD0B1',
  toneTealBg: '#D9E3FC',
  toneTealBgSoft: '#E4EAFD',
  toneTealFg: '#1C2D46',
  toneTealBorder: '#A1B8F7',
  toneEmeraldBg: '#E1E9CF',
  toneEmeraldBgSoft: '#E6EFE6',
  toneEmeraldFg: '#33502F',
  toneEmeraldBorder: '#9CC0AC',
  toneEmeraldBorderSoft: '#C3DAC8',
  toneGreenBg: '#E6EFE6',
  toneGreenFg: '#33502F',
  toneGreenBorder: '#C3DAC8',
  // blue → Inverse (Delivered, the terminal state); purple → Coral (Paused).
  // Re-pointed 2026-09-03 — these three families previously shared one border hex.
  toneBlueBg: '#322F2F',
  toneBlueFg: '#FEFFFF',
  toneBlueBorder: '#322F2F',
  tonePurpleBg: '#FFE8E9',
  tonePurpleFg: '#8F2F3A',
  tonePurpleBorder: '#F0BABC',

  // ── Tonal card families (tint = card bg, shade = fg, muted = secondary fg) ──
  tonalBlueTint: '#D9E3FC',
  tonalBlueShade: '#1C2D46',
  tonalBlueMuted: '#55647D',
  tonalSageTint: '#E1E9CF',
  tonalSageShade: '#33502F',
  tonalSageMuted: '#526C4C',
  tonalCoralTint: '#FFE8E9',
  tonalCoralShade: '#8F2F3A',
  tonalCoralMuted: '#A3505A',
  tonalPeachTint: '#FFEADB',
  tonalPeachShade: '#8A4A12',
  tonalPeachMuted: '#975C28',
  tonalNeutralTint: '#F5F5F4',
  tonalNeutralShade: '#322F2F',
  tonalNeutralMuted: '#706E6E',
  tonalInverseTint: '#322F2F',
  tonalInverseShade: '#FEFFFF',
  tonalInverseMuted: '#C5C5C5',

  // ── Signal sources (NPJB-714) — the prototype's journey chips, tags and share swatches.
  // Only the values no existing token held; the rest reuse tone/chart tokens.
  signalEmailInk: '#3D6457',
  signalWebViewsInk: '#A8474C',
  signalPublicationsTint: '#FFE4D0',
  signalPublicationsInk: '#A85A14',
  signalSocialTint: '#89AEA3',
  signalTechnographicTint: '#93AEF5',

  // ── Charts (identity series, semantic reserved, 5-step ramps, anatomy) ──
  chartSeries1: '#4271EF',
  chartSeries2: '#DD8434',
  chartSeries3: '#4E7D6E',
  chartSeries4: '#E58589',
  chartSeriesOther: '#8A8A8A',
  chartPositive: '#4E7D6E',
  chartNegative: '#E58589',
  chartWarning: '#DD8434',
  chartNeutral: '#8A8A8A',
  chartGrid: '#EDEDED',
  chartBaseline: '#C9C9C9',
  rampBlue1: '#E4EAFD',
  rampBlue2: '#CFDBFB',
  rampBlue3: '#A1B8F7',
  rampBlue4: '#7194F3',
  rampBlue5: '#4271EF',
  rampSage1: '#E6EFE6',
  rampSage2: '#C3DAC8',
  rampSage3: '#9CC0AC',
  rampSage4: '#4E7D6E',
  rampSage5: '#33502F',
  rampCoralSoft: '#F0BABC', // diverging-judged step 2
  rampSageSoft: '#A8C0B8', // diverging step (both diverging ramps)

  // ── Blue-muted scale (extended — used by audience-metrics-card) ──
  blueMutedDark: '#445064',
  blueMutedDarker: '#364050',
  blueMutedLight: '#7D8CA5',
  blueMutedLighter: '#A5B0C2',

  // ── Neutral text/border ──
  neutralIcon: '#C9C9C9',
  neutralButton: '#767676',
  neutralButtonHover: '#5A5A5A',

  // ── Success dim (button hover) ──
  successDim: '#33502F',

  // ── Filter tag categories ──
  // Text on each fill must reach 4.5:1 (NPJB-806). Industry keeps Link Blue on a lighter fill
  // (Bright Blue 10% over white; was #E4EAFD, 4.41:1 → 4.68:1). Seniority takes the sage
  // shade (was #4E7D6E, 3.98:1 → 7.66:1).
  tagIndustryBg: '#ECF1FD',
  tagIndustry: '#3462E0',
  tagSeniorityBg: '#E6EFE6',
  tagSeniority: '#33502F',
  tagJobFunctionBg: '#D9E3FC',
  tagJobFunction: '#1C2D46',
  tagIntentBg: 'rgba(66, 113, 239, 0.12)',
  tagIntent: '#322F2F',
  tagSuppressionBg: '#FFE8E9',
  tagSuppression: '#8F2F3A',

  // ── Typography families ──
  fontDisplay: "'Bodoni Moda', 'Bodoni MT', Georgia, serif", // emphasis / feeling words (italic)
  fontHeading: "'Futura PT', 'Jost', Verdana, sans-serif", // display titles + stat callouts
  fontBody: "'Roboto', Arial, sans-serif",
  fontMono: "'DM Mono', 'Courier New', monospace", // ref codes / machine identifiers only
  fontFigures: "'Roboto Mono', 'DM Mono', 'Courier New', monospace", // figures: counts, ranks, KPI values (NPJB-852)

  // ── Typography scale (px; consumed by antd, mirrored to rem in variables.css) ──
  // 8px is the floor and exists for one job: the mono micro-labels the design uses for
  // eyebrows and read-only badges (9px and 8.5px in the prototype, both → 8 on the grid).
  // Never body copy — it is well under a readable size for a sentence.
  fontSize3xs: 8,
  fontSize2xs: 10,
  fontSizeXs: 12,
  fontSizeSm: 14,
  fontSizeMd: 16,
  fontSizeLg: 18,
  fontSizeXl: 20,
  fontSize2xl: 24,
  fontSize3xl: 28,
  fontSize4xl: 36,
  fontSizeStat: 44, // Home results tile numbers (NPJB-897, prototype)
  fontSize5xl: 48,
  fontSize6xl: 56,
  fontSize7xl: 72,
  fontSize8xl: 96,

  // ── Typography weights ──
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,
  fontWeightBold: 700,

  // ── Spacing (px) ──
  // Base scale (powers of the design unit). space1 / space2 / etc. are the
  // primary tokens; the half-steps (space1_5 etc.) fill grid-aligned gaps
  // for compact paddings and small dimensions.
  space0_5: 2,
  space1: 4,
  space1_5: 6,
  space2: 8,
  space2_5: 10,
  space3: 12,
  space3_5: 14,
  space4: 16,
  space4_5: 18,
  space4_75: 20, // off the 16/24 base step; card interior padding
  space5: 24,
  space5_5: 22,
  space5_75: 28, // Home results empty card (NPJB-897, prototype)
  space5_875: 30, // Home activity feed, a filter with no rows (NPJB-898)
  space6: 32,
  space7: 40,
  space7_75: 46, // Home activity feed empty states (NPJB-898)
  space8: 48,
  space9: 72,
  space10: 80,
  space11: 96,

  // ── Container / layout widths (px) ──
  // Bounded content widths — used for card max-widths, modals, search inputs.
  containerXs: 128, // 8rem — column min-widths
  containerSm: 160, // 10rem — narrow inputs
  containerMd: 224, // 14rem — list panels
  containerLg: 256, // 16rem — search inputs
  containerXl: 320, // 20rem — auth card width
  container2xl: 352, // 22rem — error-fallback narrow
  container3xl: 448, // 28rem — error-fallback wide
  container4xl: 512, // 32rem — editable name max-width
  container5xl: 384, // 24rem — skeleton page-title block
  container6xl: 660, // 41.25rem — page-container medium
  container7xl: 800, // 50rem — page-container large
  container8xl: 1280, // 80rem — page-container max
  container9xl: 1440, // 90rem — page-container wide (NPJB-852)
  containerFlow: 1500, // 93.75rem — onboarding flow body: a question column beside its illustration
  containerFlowColumn: 760, // 47.5rem — onboarding flow question column
  containerFlowField: 560, // 35rem — onboarding flow field measure (ruled inputs)
  containerNarrow: 112, // 7rem — dual-banner min-width
  containerSnug: 192, // 12rem — skeleton width
  containerBrowse: 360, // 22.5rem — browse-columns body height default
  containerResults: 304, // 19rem — intent search-results list height
  containerIcpLabel: 148, // 9.25rem — ICP modal label column (NPJB-726)
  containerAuth: 440, // 27.5rem — auth-card max-width
  containerExportKey: 178, // 11.125rem — Export modal Columns pane's fixed key-label column
  containerExportScroll: 230, // 14.375rem — Export modal Preview/Columns pane scroll max-height
  containerExportCell: 260, // 16.25rem — Export modal Preview pane's per-cell max-width (ellipsis)
  containerSearch: 780, // 48.75rem — Home search column: label + field (NPJB-899)
  containerFeedMark: 124, // 7.75rem — Home activity feed mark column (NPJB-898)
  containerFeedTime: 52, // 3.25rem — Home activity feed loading time column (NPJB-898)
  modalChrome: 116, // 7.25rem — a modal's padding + title + footer; body caps subtract it (NPJB-800)
  // Modal widths (NPJB-818) — three sizes, so dialogs stop picking their own. sm: confirms and
  // short forms. md: upload and settings forms. lg: previews and two-column bodies.
  modalSm: 480, // 30rem
  modalMd: 640, // 40rem
  modalLg: 760, // 47.5rem

  // ── Icon / control sizes (px) ──
  // Width=height for icons, buttons-as-icons, small controls.
  iconXs: 12,
  iconSm: 14,
  iconMd: 20,
  iconLg: 28,
  iconXl: 36,
  icon2xl: 40,
  iconAvatar: 56,
  iconSignalChip: 30, // journey source glyph circle (NPJB-714 design)

  // ── App layout dimensions (px) ──
  headerHeight: 60, // app-header height (--dz-header-height)
  footerBarHeight: 64, // fixed EditFooterBar height (--dz-footer-bar-height)

  // ── Transform / animation offsets (rem-string for direct CSS use) ──
  transformLift: '0.125rem', // 2px hover lift on cards

  // ── Line-height scale (unitless) ──
  lineHeightTight: 1,
  lineHeightSnug: 1.15,
  lineHeightNormal: 1.3,
  lineHeightRelaxed: 1.5,
  lineHeightLoose: 1.75,
  lineHeightStat: 1.05, // Home results tile numbers (NPJB-897)
  lineHeightMark: 1.35, // Home activity feed row mark (NPJB-898)
  lineHeightProse: 1.55, // Home activity feed empty-state text (NPJB-898)
  lineHeightBody: 1.6, // the Home prototype's body line height: feed, section eyebrows (NPJB-950)

  // ── Letter-spacing scale (em) ──
  letterSpacingTight: '-0.04em',
  letterSpacingMinimal: '0.02em',
  letterSpacingNormal: '0.04em',
  letterSpacingWide: '0.06em',
  letterSpacingWider: '0.08em',
  letterSpacingCaps: '0.07em', // table headers and figure labels (NPJB-852)
  letterSpacingStat: '-0.02em', // Home results tile numbers (NPJB-897)
  letterSpacingChip: '0.01em', // Home results change chip (NPJB-897)
  letterSpacingWidest: '0.1em', // Home section eyebrow (NPJB-897)

  // ── Transition durations ──
  transitionFast: '120ms',
  transitionNormal: '150ms',
  transitionSlow: '180ms',
  transitionSlower: '200ms',
  transitionVerySlow: '2s',
  durationShimmer: '1.3s', // Home activity feed loading shimmer (NPJB-898)

  // ── Opacity scale ──
  opacityDisabled: 0.5,
  opacityDim: 0.55,
  opacityMuted: 0.6,
  opacityFull: 1,

  // ── Z-index scale ──
  zStickyBanner: 10,
  zDropdown: 100,
  zModal: 1000,

  // ── Radius scale: 4 chips/inputs · 8 nested blocks · 14 cards/modals · pill buttons/avatars/status pills ──
  borderRadius: 4,
  borderRadiusSm: 4,
  borderRadiusMd: 8,
  borderRadiusLg: 14,
  borderRadiusPanel: 6, // data panels (NPJB-852)
  borderRadiusTile: 10, // Home results tiles (NPJB-897, prototype)
  borderRadiusFocus: 2, // Home prototype focus-ring corners, 3 → 2 (NPJB-950)
  borderRadiusPill: 999,
  borderRadiusCircle: '50%',

  // ── Placeholder size scale (skeleton dimensions) ──
  placeholderXs: '0.75rem',
  placeholderSm: '1.5rem',
  placeholderMd: '3rem',
  placeholderLg: '6rem',
  placeholderXl: '9rem',
  placeholder2xl: '14rem',

  // ── Shadows (never a stroke and a shadow on the same container) ──
  shadowWhisper: '0 1px 3px rgba(0, 0, 0, 0.08)',
  shadowCard: '0 4px 16px rgba(0, 0, 0, 0.12)',
  shadowFloat: '0 8px 32px rgba(0, 0, 0, 0.18)',
  shadowAuth: '0 4px 28px rgba(0, 0, 0, 0.09)',
  shadowCardLift: '0 2px 12px 0 rgba(0, 0, 0, 0.06)',
  shadowCardHover: '0 6px 24px 0 rgba(0, 0, 0, 0.12)',
  shadowGlow: '0 0 12px 2px rgba(66, 113, 239, 0.30)',
  shadowBannerBottom: '0 -2px 8px rgba(0, 0, 0, 0.12)',
  shadowWizardBar: '0 -4px 12px rgba(50, 47, 47, 0.04)', // wizard-bottom-bar charcoal-tinted lift
  shadowTile: '0 1px 2px rgba(50, 47, 47, 0.04)', // Home results tiles, with their hairline (NPJB-897)
  // Doubles a ruled field's 1px underline to 2px on focus without moving the
  // text: a second line drawn directly beneath the border, same colour.
  shadowFieldFocus: '0 1px 0 0 #4271EF',
  shadowMetricCta: '0 2px 8px rgba(85, 100, 125, 0.25)', // metric-cta button blue-muted lift
} as const;

export type DesignTokens = typeof digitalzoneTokens;
